/**
 * Online battles ("Просто грати" through the backend's rooms, design D5): the battle init the server builds from a
 * room when it starts a session, the PvP maps (ONLINE_MAPS) with the facts about them that both the server and the
 * game need, the checks of online input the server applies to every relayed command, and the check of a finished
 * battle's turn structure against the moves its simulation made.
 *
 * An online battle is the original hot-seat "just" battle (new_interf go_just -> FWP.init_just_play) played by several
 * clients in lockstep: every client and the server's battle simulator run the same deterministic simulation from the
 * same init, seed and input, in a NEUTRAL battle context (no player's perks, specialization, implant, tiredness or
 * difficulty; src/game/runtime/online-battle.ts), so no personal profile can change the simulation.
 *
 * Browser-safe and platform-neutral: plain data and pure functions, no imports beyond src/session.
 */
import type { CommandLogData } from './command-log';
import type { InputFrame } from './input';
import { WIDE_MARGIN_X, WIDE_MARGIN_Y } from '../shared/wide-view';

/** One team of an online battle (OnlineBattleInit.teams), in the shape of new_interf.barracks.team<N>. */
export interface OnlineBattleTeam {
  /** Team number 1..4 (proc team index: the turn order of the battle; team 1 moves first). */
  team: number;
  /** Team name (1..20 characters). */
  name: string;
  /** Colour index 1..7 (proc.game_get_color). */
  color: number;
  /** Worms 1..8 (at most 12 in the whole battle). */
  worms: number;
  /** 0 = a human team; 1..6 = the game's AI with that noise level (COREAN_RANDOM index), simulated by every client. */
  cpu_level: number;
  /** Player who controls the team; null = the room's host (a team without a player, or an AI team). */
  playerId: string | null;
}

/**
 * Everything an online battle is built from besides its seed, fixed by the server at room.start (session.started
 * carries it with the seed as SessionInfo.online). The worms of every team are the barracks worms of go_just:
 * attack 0, protect 0, health 100, no spec, no perks, named "Червь #<n>"; every team gets the GUM_DEF arsenal.
 */
export interface OnlineBattleInit {
  kind: 'online';
  mode: 'just';
  /** The map of the battle: the stable id of a PvP map (ONLINE_MAPS). */
  mapId: OnlineMapId;
  /**
   * The same map's number in the PvP list (ONLINE_MAPS, "Карта №N"). The field predates mapId (protocol 1: a field
   * never disappears), so the server sends both; a record of an older version has only mapIndex (1..5).
   */
  mapIndex: number;
  /** 2..4 teams ordered by `team` (1..n). */
  teams: OnlineBattleTeam[];
  /** TURNTIME of the battle in seconds (45: proc.init_gameplay never shortens it for 'just'). */
  turnTimeSec: number;
  /** The battle runs in the neutral battle context on every client and in the simulator. */
  neutral: true;
}

/** The stable id of a PvP map: one of ONLINE_MAP_IDS ('wormville1' .. 'sheovar5'). */
export type OnlineMapId = string;

/**
 * The regions of the game's world whose terrains are the non-story battle maps (docs/backend.md, "Карти PvP"): five
 * maps each (terrain `<family><n>`, asset bundle map_<family>_<n>), played by the career, the survival stages and the
 * offline "Просто играть". The mission, boss, assault and forbidden maps belong to the story and are not PvP maps.
 */
export type OnlineMapFamily = 'wormville' | 'molivand' | 'rolitrad' | 'traver' | 'sheovar';

/** A PvP map: what the room, the battle init and every client need to know of it. */
export interface OnlineMap {
  /** Stable id: the game's terrain id `<family><number>` (TEAMS_DEF *.map; the asset bundle map_<family>_<number>). */
  mapId: OnlineMapId;
  /**
   * The map's number in the PvP list ("Карта №N", RoomSettings.mapIndex). Maps 1..5 are the five maps of the
   * original "Просто играть" (TEAMS_DEF just1..5: the first map of every family, in family order); map N is map
   * ceil(N / 5) of family (N - 1) % 5, so the list only ever grows at its end.
   */
  mapIndex: number;
  family: OnlineMapFamily;
  /** 1..5 within the family: the row of its minimap (interf small_<family>) and its block of spawn points (world.get_coord_shift). */
  number: number;
  /** Width of the terrain bitmap in px (the world's LIMIT_RIGHT is width + 800). */
  width: number;
  /** world.init's landscape (bg_type), background (bg_mode) and water, as every stage on this terrain has them (TEAMS_DEF). */
  bgType: string;
  bgMode: string;
  water: 'normal';
}

/** The families in the order of the original "Просто играть" maps 1..5, with their landscape and terrain widths. */
const FAMILIES: readonly { family: OnlineMapFamily; bgType: string; widths: readonly number[] }[] = [
  { family: 'wormville', bgType: 'grass', widths: [1300, 1278, 1300, 1300, 1201] },
  { family: 'molivand', bgType: 'caves', widths: [1300, 1300, 1300, 1300, 1300] },
  { family: 'rolitrad', bgType: 'snow', widths: [1300, 1300, 1300, 1300, 1300] },
  { family: 'traver', bgType: 'gulf', widths: [1300, 1300, 1300, 1300, 1300] },
  { family: 'sheovar', bgType: 'desert', widths: [1300, 1300, 1300, 1300, 1300] },
];

/** Every PvP map, ordered by mapIndex (1..25). Append-only: an id and a number never change their map. */
export const ONLINE_MAPS: readonly OnlineMap[] = Object.freeze(
  Array.from({ length: FAMILIES.length * 5 }, (_, k): OnlineMap => {
    const f = FAMILIES[k % FAMILIES.length];
    const number = Math.floor(k / FAMILIES.length) + 1;
    return Object.freeze({ mapId: f.family + number, mapIndex: k + 1, family: f.family, number, width: f.widths[number - 1], bgType: f.bgType, bgMode: 'background_' + f.family, water: 'normal' as const });
  }),
);

/** The stable ids of the PvP maps, in list order. */
export const ONLINE_MAP_IDS: readonly string[] = Object.freeze(ONLINE_MAPS.map((m) => m.mapId));

/** The default map of a room: map 1 (wormville1), the first map of the original barracks screen. */
export const DEFAULT_ONLINE_MAP: OnlineMap = ONLINE_MAPS[0];

/** proc.TURNTIME of a 'just' battle (proc.init_gameplay: 20 s only for hardcore difficulty outside assault/just). */
export const ONLINE_TURN_TIME_SEC = 45;

/** Worms of a battle at most (new_interf add_team / add_worm: barracks.worms < 12). */
export const ONLINE_MAX_WORMS = 12;

/** The game's window (FWP.WINDOW_WIDTH / WINDOW_HEIGHT). */
const WINDOW_WIDTH = 807;
const WINDOW_HEIGHT = 600;

/** The PvP map with this number ("Карта №N"), or null. */
export function onlineMap(mapIndex: number): OnlineMap | null {
  return ONLINE_MAPS.find((m) => m.mapIndex === mapIndex) ?? null;
}

/** The PvP map with this id, or null. */
export function onlineMapById(mapId: string): OnlineMap | null {
  return ONLINE_MAPS.find((m) => m.mapId === mapId) ?? null;
}

/**
 * The PvP map a room's settings or a battle init names: by mapId, else by mapIndex (a record of an older protocol-1
 * version); when both are there they must name the same map. Null for an unknown map or a contradiction.
 */
export function onlineMapOf(ref: { mapId?: unknown; mapIndex?: unknown } | null | undefined): OnlineMap | null {
  if (!ref) return null;
  const byId = typeof ref.mapId === 'string' ? onlineMapById(ref.mapId) : null;
  const byIndex = typeof ref.mapIndex === 'number' ? onlineMap(ref.mapIndex) : null;
  if (ref.mapId !== undefined && !byId) return null;
  if (ref.mapIndex !== undefined && !byIndex) return null;
  if (byId && byIndex && byId !== byIndex) return null;
  return byId ?? byIndex;
}

/**
 * `ref` with both map fields (mapId and mapIndex) of the map it names (onlineMapOf): stored rooms and sessions of an
 * older version name it by mapIndex only. Null when the map is unknown.
 */
export function withOnlineMap<T extends { mapId?: unknown; mapIndex?: unknown }>(ref: T): (Omit<T, 'mapId' | 'mapIndex'> & { mapId: string; mapIndex: number }) | null {
  const m = onlineMapOf(ref);
  return m ? { ...ref, mapId: m.mapId, mapIndex: m.mapIndex } : null;
}

/**
 * World coordinates a double click on the map can have in a "just" battle: the area src/game/runtime/battle-input.ts
 * clickArea computes for the world limits of a regular map (world.as: LIMIT_LEFT -800, LIMIT_RIGHT width + 800,
 * LIMIT_UP -150) in the game's window and the margin the widest screen shows around it (src/shared/wide-view.ts).
 * `map`: a PvP map's id or number. Null for an unknown map.
 */
export function onlineClickBounds(map: string | number): { minX: number; maxX: number; minY: number; maxY: number } | null {
  const m = typeof map === 'string' ? onlineMapById(map) : onlineMap(map);
  if (!m) return null;
  const left = -800;
  const right = m.width + 800;
  const up = -150;
  return {
    minX: Math.min(0, left, right - WINDOW_WIDTH) - WIDE_MARGIN_X,
    maxX: Math.max(WINDOW_WIDTH, right) + WIDE_MARGIN_X,
    minY: Math.min(0, up) - WIDE_MARGIN_Y,
    maxY: WINDOW_HEIGHT + 100 + WIDE_MARGIN_Y,
  };
}

const isInt = (v: unknown, min: number, max: number): v is number => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;

/** Structural validation of an OnlineBattleInit (null = valid; a record of an older version may lack mapId). */
export function validateOnlineBattleInit(init: unknown): string | null {
  if (typeof init !== 'object' || init === null || Array.isArray(init)) return 'init: expected an object';
  const o = init as Record<string, unknown>;
  if (o.kind !== 'online') return 'init.kind: expected "online"';
  if (o.mode !== 'just') return 'init.mode: expected "just"';
  if (o.mapId !== undefined && (typeof o.mapId !== 'string' || !onlineMapById(o.mapId))) return 'init.mapId: expected a PvP map id';
  if (o.mapIndex !== undefined && !isInt(o.mapIndex, 1, ONLINE_MAPS.length)) return `init.mapIndex: expected 1..${ONLINE_MAPS.length}`;
  if (o.mapId === undefined && o.mapIndex === undefined) return 'init.mapId: expected a PvP map id';
  if (!onlineMapOf(o)) return 'init.mapIndex: not the number of init.mapId';
  if (!isInt(o.turnTimeSec, 1, 3600)) return 'init.turnTimeSec: expected seconds';
  if (o.neutral !== true) return 'init.neutral: expected true';
  if (!Array.isArray(o.teams) || o.teams.length < 2 || o.teams.length > 4) return 'init.teams: expected 2..4 teams';
  let worms = 0;
  for (let i = 0; i < o.teams.length; i++) {
    const t = o.teams[i] as Record<string, unknown>;
    const p = `init.teams[${i}]`;
    if (typeof t !== 'object' || t === null) return `${p}: expected an object`;
    if (t.team !== i + 1) return `${p}.team: expected ${i + 1}`;
    if (typeof t.name !== 'string' || t.name.length < 1 || t.name.length > 20) return `${p}.name: expected 1..20 characters`;
    if (!isInt(t.color, 1, 7)) return `${p}.color: expected 1..7`;
    if (!isInt(t.worms, 1, 8)) return `${p}.worms: expected 1..8`;
    if (!isInt(t.cpu_level, 0, 6)) return `${p}.cpu_level: expected 0..6`;
    if (t.playerId !== null && (typeof t.playerId !== 'string' || t.playerId.length === 0 || t.playerId.length > 200)) return `${p}.playerId: expected a player id or null`;
    worms += t.worms as number;
  }
  if (worms > ONLINE_MAX_WORMS) return `init.teams: at most ${ONLINE_MAX_WORMS} worms in total`;
  return null;
}

/**
 * Why `frame` cannot be input of the online battle `init`, or null. Structure is checked elsewhere
 * (validateInputFrame); this adds what an online battle allows: no surrender frame (a participant leaves with
 * session.forfeit, the battle goes on for the others), a weapon choice only of a weapon the game knows
 * (`weaponIds`: WEAPONS_DEF ids) with a rank 1..10, a map click only inside the area the camera can show.
 */
export function checkOnlineFrame(frame: InputFrame, init: { mapId?: string; mapIndex?: number }, weaponIds: ReadonlySet<number>): string | null {
  if (frame.surrender) return 'surrender_not_allowed';
  if (frame.other === 'weapon') {
    if (frame.weapon === undefined || !weaponIds.has(frame.weapon)) return 'unknown_weapon';
    if (!isInt(frame.range, 1, 10)) return 'invalid_rank';
  }
  if (frame.other === 'click') {
    const map = onlineMapOf(init);
    const b = map ? onlineClickBounds(map.mapId) : null;
    const x = frame.clickX;
    const y = frame.clickY;
    if (!b || typeof x !== 'number' || typeof y !== 'number' || x < b.minX || x > b.maxX || y < b.minY || y > b.maxY) return 'click_out_of_bounds';
  }
  return null;
}

/** A move start of a simulated battle (BattleInput.moves): the tick in which proc.new_move started it and its team. */
export interface SimulatedMove {
  tick: number;
  team: number;
  /** State hash at the move start (the participants' checkpoint of that tick), when the simulation hashed checkpoints. */
  hash?: string;
}

/**
 * The first place where a log's turn structure contradicts the moves its simulation made (see checkTurnOwnership):
 *  - 'inside': the simulation started a move of another team in `tick`, strictly inside turn `turn` (before its end),
 *    so the turn went on after the battle had handed it over;
 *  - 'end': turn `turn` ended in `tick`, where the simulation started no move or a move of another team than the next
 *    turn's.
 * `openEnd`: the log's last turn is still open (a battle in progress): a move of another team in its last known tick
 * (its throughTick) is the move whose turn end is still on its way, not a violation.
 */
export interface TurnViolation {
  turn: number;
  tick: number;
  kind: 'inside' | 'end';
  message: string;
}

export function findTurnViolation(log: CommandLogData, moves: readonly SimulatedMove[], opts: { openEnd?: boolean } = {}): TurnViolation | null {
  const starts = new Map<number, number>();
  for (const m of moves) starts.set(m.tick, m.team);
  const turns = log.turns;
  for (let i = 0; i < turns.length; i++) {
    const t = turns[i];
    const last = i === turns.length - 1;
    const end = t.endTick ?? Number.POSITIVE_INFINITY;
    for (const m of moves) {
      if (m.tick < t.startTick || m.tick > end) continue;
      if (!last && m.tick === end) continue; // the next move starts in the turn's last tick (checked below)
      if (last && opts.openEnd && t.endTick === null && m.tick >= t.throughTick) continue;
      if (m.team !== t.team) return { turn: t.turn, tick: m.tick, kind: 'inside', message: `turn ${t.turn} of team ${t.team}: the simulation started a move of team ${m.team} in tick ${m.tick}` };
    }
    if (!last) {
      const next = starts.get(t.endTick!);
      if (next === undefined) return { turn: t.turn, tick: t.endTick!, kind: 'end', message: `turn ${t.turn} of team ${t.team} ended in tick ${t.endTick}, where the simulation started no move` };
      if (next !== turns[i + 1].team) {
        return { turn: t.turn, tick: t.endTick!, kind: 'end', message: `turn ${t.turn} handed the battle to team ${turns[i + 1].team}, the simulation started a move of team ${next} in tick ${t.endTick}` };
      }
    }
  }
  return null;
}

/**
 * Checks the turn structure of a finished online battle's command log against the moves its simulation made (the
 * final re-simulation catches a participant that kept playing into another team's move, or ended a turn where the
 * simulation did not start the next move):
 *  - a turn of team X owns its ticks up to its end tick E; every move that starts strictly inside the turn is a move
 *    of X (a move of another team there means X's owner played the other team's worms);
 *  - a closed turn ends in the tick in which the simulation started the next move (the owner's simulation reported
 *    it: session.endTurn), and that move is a move of the next turn's team.
 * The last turn (open or closed by the battle's end) only needs the first rule. Returns the first violation or null.
 */
export function checkTurnOwnership(log: CommandLogData, moves: readonly SimulatedMove[]): string | null {
  return findTurnViolation(log, moves)?.message ?? null;
}
